/**
 * Enhanced GroupForm Component with RBAC Support
 *
 * Supports two modes:
 * - Platform Admins: Create groups directly (immediate)
 * - Regular Users: Submit group creation requests (requires admin approval)
 *
 * Redesigned using the new form primitives for better UX, accessibility,
 * and user-friendly validation messages.
 */

import { Box, Button, Stack, Typography, Alert, Chip } from '@mui/material';
import { useState } from 'react';
import { z } from 'zod';
import {
  createSlug,
  groupWriteSchema,
  type GroupWrite,
} from '@penticton-dev-events/shared';
import {
  FormSection,
  FormField,
  FormSuccess,
  FormSuccessPresets,
  ValidationSchemas,
  validateFormValues,
  createInitialFormState,
  type FormState,
} from '../../../components/forms';
import { useIsPlatformAdmin } from '../../auth/hooks/usePermissions';
import { useCreateGroupOrRequest } from '../hooks/useCreateGroupOrRequest';

export type GroupFormValues = GroupWrite;

export type GroupFormProps = {
  readonly onSuccess?: (
    result: { type: 'group'; groupId: string } | { type: 'request'; requestId: string },
  ) => void;
  readonly onCancel?: () => void;
  readonly organizerUid: string;
  readonly organizerName: string;
  readonly organizerEmail: string;
  readonly initialValues?: Partial<GroupFormValues>;
  readonly allowOrganizerChange?: boolean;
};

const createInitialState = (
  organizerUid: string,
  organizerName: string,
  initialValues?: Partial<GroupFormValues>,
): GroupFormValues => ({
  name: initialValues?.name ?? '',
  slug: initialValues?.slug ?? '',
  organizerUid: initialValues?.organizerUid ?? organizerUid,
  organizerName: initialValues?.organizerName ?? organizerName,
  autoAcceptMembers: initialValues?.autoAcceptMembers ?? true,
  summary: initialValues?.summary ?? '',
});

const GroupForm = ({
  onSuccess,
  onCancel,
  organizerUid,
  organizerName,
  organizerEmail,
  initialValues,
  allowOrganizerChange = false,
}: GroupFormProps) => {
  const isAdmin = useIsPlatformAdmin();
  const { createGroupOrRequest, isCreating, error, clearError } =
    useCreateGroupOrRequest();

  const [formState, setFormState] = useState<FormState<GroupFormValues>>(
    createInitialFormState(
      createInitialState(organizerUid, organizerName, initialValues),
    ),
  );
  const [slugEdited, setSlugEdited] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successResult, setSuccessResult] = useState<
    { type: 'group'; groupId: string } | { type: 'request'; requestId: string } | null
  >(null);

  const updateField = <K extends keyof GroupFormValues>(
    field: K,
    value: GroupFormValues[K],
  ) => {
    // Updating form field
    // Clear any submit errors when user starts editing
    if (error) {
      clearError();
    }

    const newValues = { ...formState.values, [field]: value };
    // Form values updated

    try {
      const validation = validateFormValues(newValues, groupWriteSchema);

      setFormState(prev => ({
        ...prev,
        values: newValues,
        errors: validation.errors,
        isValid: validation.isValid,
        isDirty: true,
        touched: { ...prev.touched, [field]: true },
      }));
    } catch (error) {
      console.error(`❌ Validation error:`, error);

      // Fallback: update state without validation to allow typing
      setFormState(prev => ({
        ...prev,
        values: newValues,
        isDirty: true,
        touched: { ...prev.touched, [field]: true },
      }));
    }
  };

  const updateNameAndSlug = (nameValue: string) => {
    const generatedSlug = createSlug(nameValue);

    const newValues = {
      ...formState.values,
      name: nameValue,
      slug: slugEdited ? formState.values.slug : generatedSlug,
    };

    try {
      const validation = validateFormValues(newValues, groupWriteSchema);

      setFormState(prev => ({
        ...prev,
        values: newValues,
        errors: validation.errors,
        isValid: validation.isValid,
        isDirty: true,
        touched: { ...prev.touched, name: true, slug: true },
      }));
    } catch (error) {
      console.error(`❌ Validation error:`, error);

      // Fallback: update state without validation to allow typing
      setFormState(prev => ({
        ...prev,
        values: newValues,
        isDirty: true,
        touched: { ...prev.touched, name: true, slug: true },
      }));
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();

    void (async () => {
      try {
        const { values } = formState;
        const trimmedName = values.name.trim();
        const slugSource = values.slug.trim().length > 0 ? values.slug : trimmedName;
        const slug = createSlug(slugSource);

        if (!slug) {
          throw new Error(
            'Unable to generate a group URL. Please adjust the name or URL.',
          );
        }

        const trimmedSummary = values.summary?.trim() ?? '';

        // Use the smart hook that handles both admin and non-admin flows
        const result = await createGroupOrRequest({
          name: trimmedName,
          slug,
          summary: trimmedSummary.length > 0 ? trimmedSummary : undefined,
          autoAcceptMembers: values.autoAcceptMembers,
        });

        // Transform result to match parent's expected type
        const transformedResult =
          result.type === 'group'
            ? { type: 'group' as const, groupId: result.data.id }
            : { type: 'request' as const, requestId: result.data.id };

        // Show success state
        setSuccessResult(transformedResult);
        setShowSuccess(true);

        // Notify parent component
        onSuccess?.(transformedResult);

        // Reset form after delay
        setTimeout(() => {
          setFormState(
            createInitialFormState(
              createInitialState(organizerUid, organizerName, initialValues),
            ),
          );
          setSlugEdited(false);
          setShowSuccess(false);
          setSuccessResult(null);
        }, 5000);
      } catch (submissionError) {
        console.error('Group creation error:', submissionError);

        // Check if it's a duplicate group name error
        if (
          submissionError instanceof Error &&
          (submissionError.message.includes('already exists') ||
            submissionError.message.includes('duplicate'))
        ) {
          // Set it as a name field error
          setFormState(prev => ({
            ...prev,
            errors: {
              ...prev.errors,
              name: submissionError.message,
            },
            touched: {
              ...prev.touched,
              name: true,
            },
          }));
        }
        // Error is also handled by the hook for general display
      }
    })();
  };

  // Success state
  if (showSuccess && successResult) {
    if (successResult.type === 'group') {
      return (
        <FormSuccess
          show={true}
          title="Group Created!"
          message="Your group has been created successfully. You can now add members and create meetings."
          variant="celebration"
          primaryAction={{
            label: 'View Group',
            onClick: () => {
              setShowSuccess(false);
              onCancel?.();
            },
          }}
          secondaryAction={{
            label: 'Create Another',
            onClick: () => {
              setFormState(
                createInitialFormState(
                  createInitialState(organizerUid, organizerName, initialValues),
                ),
              );
              setSlugEdited(false);
              setShowSuccess(false);
              setSuccessResult(null);
            },
          }}
        />
      );
    } else {
      return (
        <FormSuccess
          show={true}
          title="Request Submitted!"
          message="Your group creation request has been submitted and is awaiting admin approval. You'll be notified via email within 24-48 hours."
          variant="success"
          primaryAction={{
            label: 'View My Requests',
            onClick: () => {
              setShowSuccess(false);
              onCancel?.();
            },
          }}
          secondaryAction={{
            label: 'Submit Another',
            onClick: () => {
              setFormState(
                createInitialFormState(
                  createInitialState(organizerUid, organizerName, initialValues),
                ),
              );
              setSlugEdited(false);
              setShowSuccess(false);
              setSuccessResult(null);
            },
          }}
        />
      );
    }
  }

  const { values, errors } = formState;
  const submitError = error;

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Stack spacing={4}>
        {/* Permission Notice */}
        {!isAdmin && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            icon={<Chip label="Requires Approval" size="small" color="primary" />}
          >
            <Typography variant="body2">
              <strong>Group creation requires admin approval.</strong> Your request will
              be reviewed within 24-48 hours. You'll receive an email notification once
              it's been reviewed.
            </Typography>
          </Alert>
        )}

        {isAdmin && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Admin Mode:</strong> Your group will be created immediately without
              requiring approval.
            </Typography>
          </Alert>
        )}

        {/* Basic Information Section */}
        <FormSection
          title="Basic Information"
          subtitle="Essential details about your group"
          required
        >
          <FormField
            name="name"
            label="Group Name"
            value={values.name}
            onChange={value => {
              updateNameAndSlug(value);
            }}
            validation={{
              schema: ValidationSchemas.groupName,
              required: true,
            }}
            placeholder="Enter a descriptive name for your group"
            helperText="This name is shown to members and visitors"
            maxLength={80}
            showCharacterCount
            errorMessage={errors.name}
          />

          <FormField
            name="slug"
            label="Group URL"
            value={values.slug}
            onChange={value => {
              const nextSlug = createSlug(value);
              updateField('slug', nextSlug);
              setSlugEdited(nextSlug.length > 0);
            }}
            validation={{
              schema: ValidationSchemas.groupSlug,
              required: true,
            }}
            placeholder="group-url-slug"
            helperText="Used in the group URL. Only lowercase letters, numbers, and hyphens"
            maxLength={120}
            showCharacterCount
            errorMessage={errors.slug}
          />

          <FormField
            name="summary"
            label="Group Summary"
            type="textarea"
            value={values.summary ?? ''}
            onChange={value => updateField('summary', value)}
            validation={{
              schema: z
                .string()
                .max(280, 'Summary must be 280 characters or less')
                .optional(),
            }}
            placeholder="Brief description of your group's purpose and activities"
            helperText="Optional description to help people understand what your group is about"
            minRows={3}
            maxRows={6}
            maxLength={280}
            showCharacterCount
            errorMessage={errors.summary}
          />
        </FormSection>

        {/* Group Settings Section */}
        <FormSection title="Group Settings" subtitle="Configure how your group operates">
          <FormField
            name="autoAcceptMembers"
            label="Automatically accept new members"
            type="switch"
            value={values.autoAcceptMembers}
            onChange={value => updateField('autoAcceptMembers', value)}
            helperText="When enabled, people can join immediately. When disabled, you'll need to approve each request"
          />
        </FormSection>

        {/* Organizer Section - only shown when allowed to change */}
        {allowOrganizerChange && (
          <FormSection title="Organizer" subtitle="Set who manages this group">
            <FormField
              name="organizerUid"
              label="Organizer User ID"
              value={values.organizerUid}
              onChange={value => updateField('organizerUid', value)}
              validation={{
                schema: z.string().min(1, 'Organizer UID is required'),
                required: true,
              }}
              placeholder="Enter the organizer's user ID"
              helperText="The UID of the user who will manage this group"
              errorMessage={errors.organizerUid}
            />
          </FormSection>
        )}

        {/* Organizer info */}
        {!allowOrganizerChange && (
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Organizer: {values.organizerName}
            </Typography>
          </Box>
        )}

        {/* Submit Error */}
        {submitError && (
          <Typography color="error" variant="body2" role="alert">
            {submitError.message}
          </Typography>
        )}

        {/* Form Actions */}
        <Stack direction="row" gap={2} justifyContent="flex-end" sx={{ pt: 2 }}>
          {onCancel && (
            <Button onClick={onCancel} variant="text" size="large" disabled={isCreating}>
              Cancel
            </Button>
          )}
          <Button
            disabled={isCreating || !formState.isValid}
            type="submit"
            variant="contained"
            size="large"
          >
            {isCreating
              ? isAdmin
                ? 'Creating Group...'
                : 'Submitting Request...'
              : isAdmin
                ? 'Create Group'
                : 'Request Group Creation'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default GroupForm;
